/**
 * O processo que agrupa exames (E126.F4, GUS-105, ADR-0088). Espelha `apps/api/types/processos.py`.
 */
import type { AxiosInstance } from 'axios';

import type { CaseStatus, Modalidade } from '@/lib/cases';
import { classifyExameEletronicoError } from '@/lib/exame_eletronico';

export interface ProcessoData {
  id: string;
  escritorio_id: string;
  banco_id: string | null;
  pasta: string | null;
  process_number: string | null;
  /** E165.F1.T03 (ADR-0114 §1): o CPF do cliente informado na abertura, só os dígitos. */
  cpf_informado?: string | null;
  /** E144.F3 (ADR-0098 §2.6): o pedido do cliente — prazo processual, urgência e observação. */
  prazo_processual?: string | null;
  prazo_processual_hora?: string | null;
  urgencia_solicitada?: boolean;
  motivo_da_urgencia?: string | null;
  observacao?: string | null;
  /** E177.F1 (ADR-0131): o escritório requerente do processo aberto pela operação. */
  escritorio_requerente_id?: string | null;
  /** E177.F1: quando a mesa informou o banco depois da abertura. */
  banco_informado_em?: string | null;
}

/** `PATCH /api/processos/{id}`: o campo omitido fica como está; `null` limpa. */
export interface ProcessoUpdateData {
  pasta?: string | null;
  process_number?: string | null;
  cpf_informado?: string | null;
  prazo_processual?: string | null;
  prazo_processual_hora?: string | null;
  urgencia_solicitada?: boolean;
  motivo_da_urgencia?: string | null;
  observacao?: string | null;
}

export interface ExameDoProcessoData {
  case_id: string;
  display_code: string;
  modalidade: Modalidade;
  status: CaseStatus;
  contratos: string[];
  created_at: string;
}

export interface ProcessoDetalheData {
  processo: ProcessoData;
  exames: ExameDoProcessoData[];
}

function ehContrato(data: unknown): data is ProcessoDetalheData {
  const corpo = data as Partial<ProcessoDetalheData> | null;
  return (
    typeof corpo === 'object' &&
    corpo !== null &&
    typeof corpo.processo === 'object' &&
    Array.isArray(corpo.exames)
  );
}

async function pedir(chamada: () => Promise<{ data: unknown }>): Promise<ProcessoDetalheData> {
  let data: unknown;
  try {
    ({ data } = await chamada());
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!ehContrato(data)) throw classifyExameEletronicoError(new Error('resposta fora do contrato'));
  return data;
}

export function lerProcesso(
  client: AxiosInstance,
  processoId: string,
): Promise<ProcessoDetalheData> {
  return pedir(() => client.get(`/processos/${processoId}`));
}

/**
 * E144.F3: a identificação e o pedido gravados no processo e espelhados nos exames (a pasta, o
 * número e o prazo). O banco grava só no processo que ele abriu.
 */
export function atualizarProcesso(
  client: AxiosInstance,
  processoId: string,
  mudancas: ProcessoUpdateData,
): Promise<ProcessoDetalheData> {
  return pedir(() => client.patch(`/processos/${processoId}`, mudancas));
}
